import React, { useState, useEffect } from 'react';
import { ModernButton } from '../ui/ModernButton.tsx';

interface PDFPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  fileName?: string;
  customFooter?: React.ReactNode;
}

export const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  fileName = 'نموذج الملف',
  customFooter
}) => {
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
    };
    setIsMobile(checkMobile());
  }, []);

  if (!isOpen) return null;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Use PDF.js viewer for better mobile compatibility
  const pdfViewerUrl = isMobile 
    ? `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(pdfUrl)}`
    : pdfUrl;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" dir="rtl">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="absolute inset-2 md:inset-8 flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-base md:text-lg font-semibold text-gray-900">{fileName}</h3>
          <div className="flex items-center gap-2">
            <ModernButton
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="hidden sm:flex"
            >
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              تحميل
            </ModernButton>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* PDF Viewer */}
        <div className="flex-1 relative bg-gray-100 overflow-hidden">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-sm md:text-base text-gray-600">جاري تحميل الملف...</p>
              </div>
            </div>
          )}
          
          <iframe
            src={pdfViewerUrl}
            className="w-full h-full"
            onLoad={() => setLoading(false)}
            title={fileName}
            allow="fullscreen"
            style={{ 
              border: 'none',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch' // Better iOS scrolling
            }}
          />
          
          {/* Mobile Download Button - Only show if no custom footer */}
          {!customFooter && isMobile && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <ModernButton
                variant="primary"
                size="sm"
                onClick={handleDownload}
                className="shadow-lg"
              >
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                تحميل الملف
              </ModernButton>
            </div>
          )}
        </div>
        
        {/* Footer */}
        {customFooter ? (
          customFooter
        ) : (
          <div className="p-2 md:p-3 bg-blue-50 border-t border-blue-100">
            <p className="text-xs text-blue-700 text-center">
              إذا لم يظهر الملف، يمكنك تحميله وفتحه بتطبيق PDF على جهازك
            </p>
          </div>
        )}
      </div>
    </div>
  );
};